/**
 * Final confirmation before the one-batch apply. Mirrors the rail's card
 * language: one card per item with a kind tag, a label line, and a
 * struck-old → green-new change line — instead of prose bullets.
 */
import React from "react";
import type { WorkbenchResult } from "../../../../../models";
import type { RecoveryDesc } from "../CascadeWorkbenchModal";
import { formatDateMonthDay, formatSignedDays } from "../../../../../utils/format";
import "./ConfirmSummaryModal.css";

interface Props {
  result: WorkbenchResult;
  headerLabel?: string;
  initialLogicEditCount: number;
  recovery: RecoveryDesc[];
  initialImpacted: number | null;
  applying: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

const MAX_MOVERS_SHOWN = 6;

const dateRange = (start: string | null, end: string | null) =>
  `${formatDateMonthDay(start)} – ${formatDateMonthDay(end)}`;

const ConfirmSummaryModal: React.FC<Props> = ({
  result,
  headerLabel,
  initialLogicEditCount,
  recovery,
  initialImpacted,
  applying,
  onBack,
  onConfirm,
}) => {
  const src = result.source;
  const movers = result.rows.filter((r) => r.moved);
  const cp = result.summary;
  const srcMoved =
    src != null && (src.old_end !== src.new_end || src.old_start !== src.new_start);

  return (
    // stopPropagation: this overlay sits inside the workbench overlay — a
    // backdrop click must step back, not bubble out and cancel the workbench.
    <div
      className="cwb-confirm-overlay"
      onClick={(e) => {
        e.stopPropagation();
        if (!applying) onBack();
      }}
    >
      <div className="cwb-confirm" onClick={(e) => e.stopPropagation()}>
        <h2>Confirm schedule changes</h2>

        <div className="cwb-confirm-section-title">The change under review</div>
        {src && (
          <div className="cwb-card cwb-card--source">
            <div className="cwb-card-label">
              <b>{src.task_code}</b> {src.task_name}
            </div>
            {srcMoved ? (
              <div className="cwb-change-line">
                <span className="cwb-was">{dateRange(src.old_start, src.old_end)}</span>
                <span className="cwb-now">{dateRange(src.new_start, src.new_end)}</span>
                <span className={`cwb-delta-chip cwb-delta-chip--sm${src.finish_delta < 0 ? " is-early" : ""}`}>
                  {formatSignedDays(src.finish_delta)}
                </span>
              </div>
            ) : (
              <div className="cwb-confirm-muted">no date change</div>
            )}
            {result.actuals_summary && (
              <div className="cwb-confirm-muted">
                {result.actuals_summary.actual_start &&
                  `started ${formatDateMonthDay(result.actuals_summary.actual_start)}`}
                {result.actuals_summary.actual_start &&
                  result.actuals_summary.actual_finish && " · "}
                {result.actuals_summary.actual_finish &&
                  `finished ${formatDateMonthDay(result.actuals_summary.actual_finish)}`}
              </div>
            )}
          </div>
        )}
        {initialLogicEditCount > 0 && (
          <div className="cwb-card cwb-card--source">
            <div className="cwb-card-label">
              {headerLabel ??
                `${initialLogicEditCount} dependency edit${initialLogicEditCount === 1 ? "" : "s"}`}
            </div>
          </div>
        )}

        <div className="cwb-confirm-section-title">
          Your recovery edits ({recovery.length})
        </div>
        {recovery.length === 0 ? (
          <div className="cwb-confirm-muted cwb-confirm-empty">none</div>
        ) : (
          recovery.map((d, i) => (
            <div key={i} className="cwb-card cwb-card--recovery">
              <div className="cwb-card-kind">
                {d.kind === "dur" ? "Duration" : "Dependency"}
              </div>
              <div className="cwb-card-label" title={d.label}>
                {d.label}
              </div>
              <div className="cwb-change-line">
                <span className="cwb-was">{d.was}</span>
                <span className="cwb-now">{d.now}</span>
              </div>
            </div>
          ))
        )}

        <div className="cwb-confirm-section-title">Resulting schedule movement</div>
        <div className="cwb-confirm-summary-line">
          <b>{movers.length}</b> dependent {movers.length === 1 ? "activity" : "activities"}{" "}
          {movers.length === 1 ? "moves" : "move"}
          {initialImpacted != null && initialImpacted !== movers.length && (
            <span className="cwb-confirm-recovered">
              {" "}
              (was {initialImpacted} before recovery)
            </span>
          )}
        </div>
        {movers.slice(0, MAX_MOVERS_SHOWN).map((r) => (
          <div key={r.activity_id} className="cwb-confirm-mover">
            <span className="cwb-confirm-mover-name" title={r.task_name}>
              <b>{r.task_code}</b> {r.task_name}
            </span>
            <span className="cwb-confirm-mover-date">
              → {formatDateMonthDay(r.new_end)}
            </span>
            <span className={`cwb-delta-chip cwb-delta-chip--sm${r.days_pushed < 0 ? " is-early" : ""}`}>
              {formatSignedDays(r.days_pushed)}
            </span>
          </div>
        ))}
        {movers.length > MAX_MOVERS_SHOWN && (
          <div className="cwb-confirm-muted cwb-confirm-empty">
            … and {movers.length - MAX_MOVERS_SHOWN} more
          </div>
        )}
        {result.constraint_violations.length > 0 && (
          <div className="cwb-confirm-verdict is-warn">
            ⚠ {result.constraint_violations.length} constraint violation
            {result.constraint_violations.length === 1 ? "" : "s"}
          </div>
        )}
        <div className={`cwb-confirm-verdict${cp.critical_path_impact ? " is-warn" : " is-ok"}`}>
          {cp.critical_path_impact
            ? `⚠ still affects ${cp.cp_count} critical-path ${cp.cp_count === 1 ? "activity" : "activities"}`
            : "✓ no critical-path impact"}
        </div>

        <div className="cwb-confirm-note">
          Everything above commits as <b>one batch</b> — a single rollback from the
          Changes tab undoes all of it.
        </div>

        <div className="cwb-confirm-actions">
          <button className="cwb-btn" onClick={onBack} disabled={applying}>
            Back
          </button>
          <button
            className="cwb-btn cwb-btn--primary"
            onClick={onConfirm}
            disabled={applying}
          >
            {applying ? "Applying…" : "Confirm & Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSummaryModal;
